import { useEffect, useMemo, useState } from 'react'
import { usePageActions, usePageTitle } from '../AppLayout'
import { api } from '../api/client'
import type { NewsQuery } from '../api/client'
import { useHasPermission } from '../api/session'
import { useListParams } from '../hooks/useListParams'
import { useEndlessList } from '../hooks/useEndlessList'
import { useVerknuepft } from '../hooks/useVerknuepft'
import { Button } from '../components/common/Button'
import { Input, Select } from '../components/common/Controls'
import { EmptyState, ErrorBox, Loading, Note } from '../components/common/Feedback'
import { FilterBar, FilterEnd, FilterFixed, FilterGrow }
  from '../components/common/FilterBar'
import { PermissionNotice } from '../components/common/PermissionNotice'
import { Umschalter } from '../components/common/Umschalter'
import { MasterDetail } from '../components/layout/MasterDetail'
import { NewsListe, gruppiere } from '../components/news/NewsListe'
import { NewsDetail } from '../components/news/NewsDetail'
import s from './NewsPage.module.scss'

const PERMISSION = 'news:read'
const PAGE_SIZE = 50

type Sicht = 'liste' | 'gruppen'

const SICHTEN: { wert: Sicht; text: string }[] = [
  { wert: 'liste', text: 'Liste' },
  { wert: 'gruppen', text: 'Nach Betrifft' },
]

/** Aus einem Eingabefeld wird eine Liste — oder gar kein Filter. */
function einer(wert: string): string[] | undefined {
  const gestrafft = wert.trim().toUpperCase()
  return gestrafft ? [gestrafft] : undefined
}

/**
 * Die Meldungen als Master–Detail.
 *
 * LINKS die Liste, RECHTS die gewählte Meldung — statt einer Tabelle mit dem
 * Detail darunter. Eine Meldung liest man ganz, und dabei will man die
 * nächste schon sehen: Pfeiltasten und die Knöpfe über dem Detail blättern,
 * die Liste hält die Auswahl markiert. Nach Betrifft gruppiert stehen alle
 * Meldungen zu einem Ziel beieinander; das Blättern folgt dann der Gruppe.
 *
 * Die Filter sind dieselben wie zuvor und stehen in der Adresse; Sicht und
 * Auswahl sind Zustand dieses Augenblicks und stehen nicht darin.
 */
export function NewsPage() {
  const allowed = useHasPermission(PERMISSION)
  const [sicht, setSicht] = useState<Sicht>('liste')
  const [gewaehlt, setGewaehlt] = useState<number | null>(null)

  const list = useListParams({
    filters: { q: '', isin: '', commodity: '', industry: '', ref: '',
               sentiment: 'all', since: '', until: '' },
    sort: 'published_at',
    descending: true,
  })

  const params: NewsQuery = {
    q: list.filters.q.trim() || undefined,
    isin: einer(list.filters.isin),
    commodity: einer(list.filters.commodity),
    industry: einer(list.filters.industry),
    ref: list.filters.ref.trim() ? [list.filters.ref.trim()] : undefined,
    sentiment: list.filters.sentiment as NewsQuery['sentiment'],
    since: list.filters.since ? `${list.filters.since}T00:00:00Z` : undefined,
    until: list.filters.until ? `${list.filters.until}T23:59:59Z` : undefined,
    sort: list.sort as NewsQuery['sort'],
    dir: list.descending ? 'desc' : 'asc',
    size: PAGE_SIZE,
    total: true,
  }

  const { rows: zeilen, loading, loadingMore, error, denied, loadMore, total }
    = useEndlessList(api.news, params, allowed)

  // Die Reihenfolge fürs Blättern: in der Liste wie geladen, gruppiert
  // Gruppe für Gruppe — so springt „nächste" nicht aus der Gruppe heraus.
  const reihe = useMemo(() => sicht === 'gruppen'
    ? gruppiere(zeilen).flatMap((g) => g.eintraege.map((e) => e.id))
    : zeilen.map((z) => z.id), [zeilen, sicht])

  // Ohne Auswahl die erste Meldung — ein leeres Detail neben einer vollen
  // Liste sähe aus, als fehlte etwas. Verschwindet die gewählte Meldung
  // durch einen Filterwechsel, rückt wieder die erste nach.
  const offen = zeilen.find((z) => z.id === gewaehlt) ?? zeilen[0] ?? null
  const index = offen ? reihe.indexOf(offen.id) : -1

  function springe(schritt: number) {
    if (reihe.length === 0) return
    const ziel = (index + schritt + reihe.length) % reihe.length
    setGewaehlt(reihe[ziel])
  }

  // Pfeiltasten blättern — außer in einem Eingabefeld, dort bewegen sie den
  // Cursor.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      const ziel = event.target as HTMLElement | null
      if (ziel && /INPUT|TEXTAREA|SELECT/.test(ziel.tagName)) return
      if (event.key === 'ArrowDown') { event.preventDefault(); springe(1) }
      if (event.key === 'ArrowUp') { event.preventDefault(); springe(-1) }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  })

  const { eintraege: verknuepft, laedt: verknuepftLaedt } = useVerknuepft(offen)

  const gruppen = useMemo(() => gruppiere(zeilen).length, [zeilen])
  usePageTitle('News', total !== null
    ? `${zeilen.length} von ${total} Meldungen · ${gruppen} Ziele`
    : 'Recherche und Marktnachrichten')
  usePageActions(useMemo(() => (
    <Umschalter optionen={SICHTEN} wert={sicht} onChange={setSicht}
                label="Darstellung der Liste" />
  ), [sicht]))

  if (!allowed || denied) {
    return <PermissionNotice permission={PERMISSION} subject="die Meldungen" />
  }

  const position = (() => {
    if (!offen) return ''
    if (sicht === 'gruppen') {
      const gruppe = gruppiere(zeilen).find((g) => g.eintraege.some((e) => e.id === offen.id))
      if (gruppe) {
        const i = gruppe.eintraege.findIndex((e) => e.id === offen.id) + 1
        return `${gruppe.name} · ${i} von ${gruppe.eintraege.length}`
      }
    }
    return `${index + 1} von ${reihe.length}`
  })()

  return (
    <>
      <FilterBar>
        <FilterGrow>
          <Input label="Suche"
                 placeholder="Überschrift oder Volltext — mindestens zwei Zeichen"
                 // OHNE autoFocus, anders als die anderen Listen: Mit dem Cursor im
                 // Suchfeld bewegten die Pfeiltasten den Cursor statt der Auswahl —
                 // und Blättern ist hier der häufigere Griff.
                 value={list.filters.q}
                 onChange={(event) => list.setFilter('q', event.target.value)} />
        </FilterGrow>
        <FilterFixed>
          <Input label="ISIN" placeholder="z. B. US4581401001"
                 value={list.filters.isin}
                 onChange={(event) => list.setFilter('isin', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Rohstoff" placeholder="z. B. BRENT"
                 value={list.filters.commodity}
                 onChange={(event) => list.setFilter('commodity', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Branche" placeholder="z. B. IW:10"
                 value={list.filters.industry}
                 onChange={(event) => list.setFilter('industry', event.target.value)} />
        </FilterFixed>
        {list.filters.ref && (
          <FilterFixed>
            <div className={s.bezug}>
              <Input label="Bezug" value={list.filters.ref} readOnly
                     title="Kommt aus dem Popover eines Verweises oder aus der Adresse" />
              <button type="button" className={s.bezugWeg}
                      aria-label="Bezug entfernen" title="Bezug entfernen"
                      onClick={() => list.setFilter('ref', '')}>
                ✕
              </button>
            </div>
          </FilterFixed>
        )}
        <FilterFixed>
          <Input label="Von" type="date" value={list.filters.since}
                 onChange={(event) => list.setFilter('since', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Input label="Bis" type="date" value={list.filters.until}
                 onChange={(event) => list.setFilter('until', event.target.value)} />
        </FilterFixed>
        <FilterFixed>
          <Select label="Bewertung" value={list.filters.sentiment}
                  onChange={(event) => list.setFilter('sentiment', event.target.value)}>
            <option value="all">alle</option>
            <option value="positive">positiv (+1 … +3)</option>
            <option value="neutral">neutral (0)</option>
            <option value="negative">negativ (−1 … −3)</option>
          </Select>
        </FilterFixed>
        <FilterEnd>
          <Button onClick={list.reset} disabled={!list.touched}>
            Zurücksetzen
          </Button>
        </FilterEnd>
      </FilterBar>

      {error && <ErrorBox>{error}</ErrorBox>}

      <MasterDetail
        liste={loading && zeilen.length === 0 ? (
          <Loading>lädt Meldungen …</Loading>
        ) : (
          <NewsListe items={zeilen} gewaehlt={offen?.id ?? null}
                     onWaehle={setGewaehlt} gruppiert={sicht === 'gruppen'}
                     onEndReached={loadMore} loadingMore={loadingMore}
                     leer={list.touched
                       ? 'Keine Meldung passt zu diesen Filtern.'
                       : 'Es sind noch keine Meldungen erfasst.'}
                     fuss={`${zeilen.length} ${zeilen.length === 1 ? 'Meldung' : 'Meldungen'}`} />
        )}
        detail={offen ? (
          <NewsDetail item={offen} position={position}
                      onVorherige={() => springe(-1)} onNaechste={() => springe(1)}
                      verknuepft={verknuepft} verknuepftLaedt={verknuepftLaedt} />
        ) : !loading && (
          <div className={s.ohne}>
            <EmptyState title="keine Meldung gewählt">
              Links eine Meldung wählen — oder die Filter lockern.
            </EmptyState>
          </div>
        )} />

      <Note>
        Die Bewertung <b>nicht mitteln</b>: +3 und −3 ergeben im Schnitt 0, also
        scheinbar unauffällig — tatsächlich sind es zwei starke gegenläufige
        Meldungen. Der Schnitt am Gruppenkopf ist deshalb nur ein Hinweis.
      </Note>
    </>
  )
}
